import {
  read_safe_object_value,
  write_safe_object_value
} from '@/services/onboarding_form_helpers';
import {
  build_file_answer_payload,
  build_standard_answers,
  get_visible_questions,
  question_input_type as resolve_question_input_type,
  uses_checkbox as uses_checkbox_helper,
  uses_checkbox_group as uses_checkbox_group_helper,
  uses_facial_verification as uses_facial_verification_helper,
  uses_file as uses_file_helper,
  uses_radio as uses_radio_helper,
  uses_select as uses_select_helper,
  uses_textarea as uses_textarea_helper
} from './intake_portal_helpers';

function get_multi_select_values(current_value) {
  return Array.isArray(current_value) ? [...current_value] : [];
}

function add_selected_value(values = [], option_value = '') {
  if (!values.includes(option_value)) values.push(option_value);
  return values;
}

function remove_selected_value(values = [], option_value = '') {
  return values.filter((value) => value !== option_value);
}

function normalize_multi_select_values(values = []) {
  return values.filter(Boolean);
}

function is_answer_value_present(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return value;
  return String(value ?? '').trim() !== '';
}

function normalize_checkbox_group_value(value) {
  return Array.isArray(value) ? value.join(', ') : '';
}

function has_empty_answer_value(value) {
  return value === undefined || value === null || value === '';
}

function should_normalize_boolean_answer(uses_checkbox, value) {
  return uses_checkbox || typeof value === 'boolean';
}

function compact_dependency_text(value = '') {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

function reset_file_input_value(question_id) {
  if (typeof document === 'undefined') return;
  const input = document.getElementById(`question_${question_id}`);
  if (input) input.value = '';
}

export const intake_portal_methods = {
  question_input_type(question) {
    return resolve_question_input_type(question);
  },
  uses_textarea(question) {
    return uses_textarea_helper(question);
  },
  uses_select(question) {
    return uses_select_helper(question);
  },
  uses_radio(question) {
    return uses_radio_helper(question);
  },
  uses_checkbox_group(question) {
    return uses_checkbox_group_helper(question);
  },
  uses_file(question) {
    return uses_file_helper(question);
  },
  uses_facial_verification(question) {
    return uses_facial_verification_helper(question);
  },
  uses_checkbox(question) {
    return uses_checkbox_helper(question);
  },
  get_facial_verification_value(question_id) {
    return read_safe_object_value(this.facial_verification_values, question_id, null);
  },
  update_facial_verification_value(question_id, value) {
    write_safe_object_value(this.facial_verification_values, question_id, value);
  },
  update_multi_select_value(question_id, option_value, is_checked) {
    const current_value = read_safe_object_value(this.form_values, question_id, []);
    const existing_values = get_multi_select_values(current_value);
    const updated_values = is_checked
      ? add_selected_value(existing_values, option_value)
      : remove_selected_value(existing_values, option_value);
    write_safe_object_value(this.form_values, question_id, normalize_multi_select_values(updated_values));
  },
  handle_file_change(question_id, event) {
    const selected_files = Array.from(event?.target?.files || []);
    const existing_files = read_safe_object_value(this.file_values, question_id, []);
    const next_files = [
      ...(Array.isArray(existing_files) ? existing_files.filter((file) => !file?.removed) : existing_files ? [existing_files] : []),
      ...selected_files
    ];
    write_safe_object_value(this.file_values, question_id, next_files);
    if (event?.target) event.target.value = '';
  },
  clear_selected_file(question_id) {
    write_safe_object_value(this.file_values, question_id, null);
    reset_file_input_value(question_id);
  },
  format_file_name(file, question_id) {
    const files = Array.isArray(file) ? file.filter((item) => item && !item.removed) : file ? [file] : [];
    if (files.length) return `${files.length} ${files.length === 1 ? 'file' : 'files'} selected`;
    return read_safe_object_value(this.persisted_file_urls, question_id, '') || 'Choose files';
  },
  visible_question_files(question_id) {
    const files = read_safe_object_value(this.file_values, question_id, []);
    return (Array.isArray(files) ? files : files ? [files] : [])
      .filter((file) => file && !file.removed);
  },
  remove_question_file(question_id, file_index) {
    const files = read_safe_object_value(this.file_values, question_id, []);
    const next_files = Array.isArray(files) ? [...files] : files ? [files] : [];
    const target_file = Reflect.get(this.visible_question_files(question_id), file_index);
    const target_index = next_files.indexOf(target_file);
    if (!target_file || target_index < 0) return;

    if (target_file.persisted) {
      Reflect.set(next_files, target_index, { ...target_file, removed: true });
    } else {
      next_files.splice(target_index, 1);
    }
    write_safe_object_value(this.file_values, question_id, next_files);
  },
  get_question_value(question) {
    if (this.uses_checkbox_group(question)) {
      const value = read_safe_object_value(this.form_values, question.id, []);
      return Array.isArray(value) ? value : [];
    }
    if (this.uses_checkbox(question)) return Boolean(read_safe_object_value(this.form_values, question.id, false));
    return read_safe_object_value(this.form_values, question.id, '');
  },
  get_current_step_questions() {
    return this.current_main_section?.sub_sections?.flatMap((sub_section) => this.visible_questions_for_sub_section(sub_section)) || [];
  },
  visible_questions_for_sub_section(sub_section) {
    return get_visible_questions(sub_section?.questions || [], this.form_values);
  },
  is_dependent_question(question) {
    return Boolean(question?.is_dependent_question);
  },
  dependent_question_hint(question) {
    if (!this.is_dependent_question(question)) return '';
    const parent_label = compact_dependency_text(question?.parent_question_label || 'the previous answer');
    const trigger = compact_dependency_text(question?.trigger_condition);
    return trigger
      ? `Shown when ${parent_label} is "${trigger}".`
      : `Shown based on ${parent_label}.`;
  },
  is_question_answered(question) {
    if (!question?.id) return true;
    if (this.uses_facial_verification(question)) {
      const value = this.get_facial_verification_value(question.id);
      return Boolean(value?.completed);
    }
    const persisted_file = read_safe_object_value(this.persisted_file_urls, question.id, '');
    if (this.uses_file(question)) return Boolean(this.visible_question_files(question.id).length || persisted_file);
    const value = read_safe_object_value(this.form_values, question.id, '');
    return is_answer_value_present(value);
  },
  validate_current_step() {
    const missing_question = this.get_current_step_questions().find((question) => question.is_mandatory && !this.is_question_answered(question));
    if (!missing_question) {
      this.portal_error = '';
      return true;
    }
    this.portal_error = `Complete ${missing_question.label} before continuing.`;
    return false;
  },
  normalize_boolean_answer(value) {
    return value ? 'Yes' : 'No';
  },
  normalize_answer_value(question, value) {
    if (has_empty_answer_value(value)) return '';
    if (this.uses_checkbox_group(question)) return normalize_checkbox_group_value(value);
    if (should_normalize_boolean_answer(this.uses_checkbox(question), value)) return this.normalize_boolean_answer(value);
    return String(value).trim();
  },
  buildStandardAnswers() {
    return build_standard_answers(
      this.intake_steps,
      (question) => this.uses_file(question),
      (question, value) => this.normalize_answer_value(question, value),
      this.form_values,
      (question) => this.uses_facial_verification(question)
    );
  },
  buildFileAnswerPayload() {
    return build_file_answer_payload(
      this.intake_steps,
      (question) => this.uses_file(question),
      this.file_values,
      this.form_values,
      (question) => this.uses_facial_verification(question)
    );
  }
};
